import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sparkles, Trophy, Users, Menu, X, LogOut, User, LayoutDashboard, Gift, Sun, Moon, PawPrint } from "lucide-react";
import { useFinFoxRunner } from "@/hooks/useFinFoxRunner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import FinPlayLogo from "./FinPlayLogo";
import XPStreakWidget from "./layout/XPStreakWidget";
import { useTheme } from "next-themes";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { prefetchRoute } from "@/components/PrefetchLink";


interface NavItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  href: string;
  highlight?: boolean;
}

const navItems: NavItem[] = [
  { icon: Sparkles, label: "Fin-der", href: "/financial-tinder", highlight: true },
  { icon: Trophy, label: "League", href: "/financial-league" },
  { icon: Users, label: "Community", href: "/community" },
];

const TopNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const finfox = useFinFoxRunner();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  const { setTheme, resolvedTheme } = useTheme();

  return (
    <>
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          isScrolled
            ? "bg-background/80 border-b border-border/50"
            : "bg-transparent"
        )}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <FinPlayLogo size="md" animated showText={!isMobile} />
            </Link>

            {!isMobile && (
              <nav className="flex items-center gap-1">
                {navItems.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onMouseEnter={() => prefetchRoute(item.href)}
                      onFocus={() => prefetchRoute(item.href)}
                      className={cn(
                        "relative flex items-center gap-2 px-4 py-2 rounded-full",
                        "font-medium text-sm transition-all duration-300",
                        "hover:bg-accent/10",
                        active ? "text-primary" : "text-muted-foreground hover:text-foreground",
                        item.highlight && !active && "text-secondary"
                      )}
                    >
                      <item.icon className={cn("h-4 w-4", item.highlight && !active && "text-secondary")} />
                      <span>{item.label}</span>
                      {active && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />}
                    </Link>

                  );
                })}
              </nav>
            )}

            <div className="flex items-center gap-2">
              {user ? (
                <>
                  {!isMobile && <XPStreakWidget />}

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 hover:ring-2 hover:ring-primary/20 transition-all">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.user_metadata?.avatar_url} />
                          <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                            {user.email?.[0]?.toUpperCase() || "U"}
                          </AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-2 py-1.5">
                        <p className="text-sm font-medium">{user.user_metadata?.full_name || "User"}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onMouseEnter={() => prefetchRoute("/dashboard")} onFocus={() => prefetchRoute("/dashboard")} onClick={() => navigate("/dashboard")}>
                        <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onMouseEnter={() => prefetchRoute("/profile")} onFocus={() => prefetchRoute("/profile")} onClick={() => navigate("/profile")}>
                        <User className="mr-2 h-4 w-4" /> Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem onMouseEnter={() => prefetchRoute("/rewards-center")} onFocus={() => prefetchRoute("/rewards-center")} onClick={() => navigate("/rewards-center")}>
                        <Gift className="mr-2 h-4 w-4" /> Rewards
                      </DropdownMenuItem>

                      <DropdownMenuItem onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}>
                        {resolvedTheme === "dark" ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                        {resolvedTheme === "dark" ? "Light Mode" : "Dark Mode"}
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={(e) => { e.preventDefault(); finfox.toggle(); }}>
                        <PawPrint className="mr-2 h-4 w-4" />
                        {finfox.enabled ? "Turn off FinFox runner" : "Turn on FinFox runner"}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                        <LogOut className="mr-2 h-4 w-4" /> Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <Button
                  onClick={() => navigate("/auth")}
                  onMouseEnter={() => prefetchRoute("/auth")}
                  onFocus={() => prefetchRoute("/auth")}
                  size="sm"
                  className="rounded-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
                >
                  Get Started
                </Button>

              )}

              {isMobile && (
                <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(true)} className="h-9 w-9">
                  <Menu className="h-5 w-5" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {isMobile && mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] bg-background/95">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <FinPlayLogo size="md" showText />
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <nav className="flex-1 p-4 space-y-2">
              {navItems.map((item) => {
                const active = isActive(item.href);
                return (
                  <button
                    key={item.href}
                    onClick={() => { navigate(item.href); setMobileMenuOpen(false); }}
                    onTouchStart={() => prefetchRoute(item.href)}
                    onMouseEnter={() => prefetchRoute(item.href)}
                    onFocus={() => prefetchRoute(item.href)}
                    className={cn(
                      "w-full flex items-center gap-4 px-4 py-4 rounded-2xl",
                      "font-medium text-lg transition-all duration-300",
                      active ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted"
                    )}
                  >
                    <item.icon className="h-6 w-6" />
                    <span>{item.label}</span>
                  </button>

                );
              })}
            </nav>
            {user && (
              <div className="p-4 border-t border-border">
                <div className="flex items-center gap-3 mb-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={user.user_metadata?.avatar_url} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {user.email?.[0]?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{user.user_metadata?.full_name || "User"}</p>
                    <p className="text-sm text-muted-foreground truncate max-w-[200px]">{user.email}</p>
                  </div>
                </div>
                <XPStreakWidget />
                <button
                  onClick={() => finfox.toggle()}
                  className="mt-4 flex w-full items-center gap-3 rounded-2xl border border-border px-4 py-3 text-left text-sm font-medium"
                >
                  <PawPrint className="h-5 w-5 text-primary" />
                  {finfox.enabled ? "Turn off FinFox runner" : "Turn on FinFox runner"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      
    </>
  );
};

export default TopNavbar;
